import type { OrderPageView } from "@shared-core/data/customer-link-adapter";
import type { OrderArrival } from "../customer-pages/OrderPageBody";

// What a signed-in CIS order page needs to work out from its read before it can call an action: which of the
// read's URLs an action of the body goes to. The body asks for "pay for this line" or "give the PO number" and
// knows nothing of URLs; the read names them, and the page never builds one.

/**
 * The URL that pays for one thing the order owes by card: the order's own payment or a later addition's
 * (`payments_due`), or a pro-forma's *Pay by card instead* (`pay_by_card_url`). `addedLineRef` is the line the
 * body names (null: the order's own). Throws when the read names no URL for it, rather than guess one: the
 * button was drawn from this same read, so a miss means the page changed under the person.
 */
export function payUrlFor(
  page: OrderPageView,
  addedLineRef: string | null,
): string {
  const due = page.payments_due.find((p) => p.added_line_ref === addedLineRef);
  const proforma = page.proformas_due.find(
    (p) => p.added_line_ref === addedLineRef,
  );
  const url = due?.pay_url ?? proforma?.pay_by_card_url ?? null;
  if (url === null)
    throw new Error(
      "This payment isn't on the order any more. Please reload the page.",
    );
  return url;
}

/** The URL the PO number goes to; throws when the order is not asking for one. */
export function poNumberUrlFor(page: OrderPageView): string {
  const url = page.po_number_owed?.url ?? null;
  if (url === null)
    throw new Error(
      "This order isn't waiting for a PO number. Please reload the page.",
    );
  return url;
}

/** What the address says about how the person got here: straight after a quote's acceptance (`?placed=1`), or
 *  back from the card checkout (`?payment=return`, which Stripe's return adds). */
export function orderArrival(searchParams: URLSearchParams): OrderArrival {
  if (searchParams.get("placed") === "1") return "placed";
  if (searchParams.get("payment") === "return") return "payment_return";
  return null;
}
